import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, TablePagination, TextField, Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import PageContainer from '../components/PageContainer';
import { useIsXs } from '../hooks/useMobileLayout';
import { ListCardField } from '../components/ListCardField';
import { listLogs } from '../api/services';
import type { ApplicationLogEntry } from '../../types/domain';
import { useAppSelector } from '../store/store';

export default function AdminLogs() {
  const isXs = useIsXs();
  const me = useAppSelector((s) => s.auth.user);
  const isSuperAdmin = !!me?.isSuperAdmin;

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [items, setItems] = useState<ApplicationLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [level, setLevel] = useState('');
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tenantId, setTenantId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number | undefined> = {
        page: page + 1, pageSize,
      };
      if (level)    params.level = level;
      if (category) params.category = category;
      if (from)     params.from = new Date(from).toISOString();
      if (to)       params.to = new Date(to).toISOString();
      if (isSuperAdmin && tenantId != null) params.tenantId = tenantId;

      const res = await listLogs(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudieron cargar los logs');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, level, category, from, to, tenantId, isSuperAdmin]);

  useEffect(() => { load(); }, [load]);

  function colorByLevel(lv: string) {
    if (lv === 'error') return 'error' as const;
    if (lv === 'warn')  return 'warning' as const;
    return 'default' as const;
  }

  return (
    <PageContainer>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2, minWidth: 0 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>Logs</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>Auditoría de requests HTTP y eventos de la aplicación.</Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<FilterListIcon />}
          onClick={() => setFiltersOpen(true)}
          fullWidth={isXs}
          sx={{ fontWeight: 700, textTransform: 'none', flexShrink: 0 }}
        >
          Filtros
        </Button>
      </Stack>

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="body">
        <DialogTitle sx={{ fontWeight: 800 }}>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Nivel</InputLabel>
              <Select label="Nivel" value={level} onChange={(e) => { setPage(0); setLevel(e.target.value); }}>
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="info">Info</MenuItem>
                <MenuItem value="warn">Warn</MenuItem>
                <MenuItem value="error">Error</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Categoría</InputLabel>
              <Select label="Categoría" value={category} onChange={(e) => { setPage(0); setCategory(e.target.value); }}>
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="http">HTTP</MenuItem>
                <MenuItem value="auth">Auth</MenuItem>
                <MenuItem value="payments">Pagos</MenuItem>
              </Select>
            </FormControl>
            {isSuperAdmin && (
              <TextField
                size="small"
                fullWidth
                label="Tenant ID"
                type="number"
                value={tenantId ?? ''}
                onChange={(e) => { setPage(0); setTenantId(e.target.value === '' ? null : Number(e.target.value)); }}
              />
            )}
            <TextField size="small" fullWidth label="Desde" type="datetime-local" value={from} onChange={(e) => { setPage(0); setFrom(e.target.value); }} InputLabelProps={{ shrink: true }} />
            <TextField size="small" fullWidth label="Hasta" type="datetime-local" value={to} onChange={(e) => { setPage(0); setTo(e.target.value); }} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setFiltersOpen(false)} fullWidth={isXs}>Listo</Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', p: { xs: 1.5, sm: 2 } }}>
        {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
          <>
            {items.length ? (
              <Grid container spacing={2}>
                {items.map((l) => (
                  <Grid key={String(l.logId)} size={{ xs: 12, md: 6 }}>
                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, '&:last-child': { pb: 2 } }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <Typography variant="caption" color="text.secondary">{new Date(l.createdAt).toLocaleString('es-AR')}</Typography>
                          <Chip size="small" color={colorByLevel(l.level)} label={l.level} />
                        </Stack>
                        <ListCardField label="Categoría">
                          <Typography variant="body2">{l.category}</Typography>
                        </ListCardField>
                        <ListCardField label="Método / Path">
                          <Box component="span" sx={{ fontSize: '0.875rem' }}>
                            {l.httpMethod || '—'}{' '}
                            <Box component="code" sx={{ fontSize: '0.8rem', wordBreak: 'break-all' }}>{l.path || '—'}</Box>
                          </Box>
                        </ListCardField>
                        <ListCardField label="Status">
                          <Typography variant="body2">{l.statusCode || '—'}</Typography>
                        </ListCardField>
                        <ListCardField label="Usuario">
                          <Typography variant="body2">{l.dni || l.userId || '—'}</Typography>
                        </ListCardField>
                        <ListCardField label="Tenant">
                          <Typography variant="body2">{l.tenantId ?? '—'}</Typography>
                        </ListCardField>
                        <ListCardField label="Mensaje">
                          <Typography variant="body2" sx={{ fontSize: '0.8rem', wordBreak: 'break-word' }}>{l.message}</Typography>
                        </ListCardField>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>Sin logs para los filtros aplicados.</Typography>
            )}
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]}
              labelRowsPerPage="Filas"
              sx={{ width: 1, overflow: 'auto', borderTop: 1, borderColor: 'divider', mt: 2, '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1, px: 1 } }}
            />
          </>
        )}
      </Paper>
    </PageContainer>
  );
}
